import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

/** Closing unmounts the overlay before mouseup — swallow the stray click on what is underneath. */
function swallowGhostPointer() {
  const block = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };
  document.addEventListener('click', block, true);
  document.addEventListener('mouseup', block, true);
  window.setTimeout(() => {
    document.removeEventListener('click', block, true);
    document.removeEventListener('mouseup', block, true);
  }, 0);
}

function runClose(action) {
  swallowGhostPointer();
  action?.();
}

/**
 * In-app confirmation modal (replaces window.confirm). Escape and backdrop
 * cancel; confirm can show a busy spinner. `tone="danger"` for destructive
 * actions, `tone="brand"` for proceed-anyway actions.
 */
export default function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  tone = 'danger', busy = false, onConfirm, onCancel, children,
}) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const focusTimer = window.setTimeout(() => cancelRef.current?.focus(), 0);

    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        runClose(onCancel);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = panelRef.current?.querySelectorAll('button:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open, busy, onCancel]);

  if (!open) return null;
  const confirmCls = tone === 'danger'
    ? 'bg-error-600 hover:bg-error-700 focus-visible:ring-error-500/40'
    : 'bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500/40';
  const iconWrap = tone === 'danger' ? 'bg-error-50 text-error-600' : 'bg-brand-50 text-brand-600';

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Cancel"
        className="absolute inset-0 bg-dark-950/50 backdrop-blur-[1px]"
        onClick={(e) => {
          e.stopPropagation();
          if (!busy) runClose(onCancel);
        }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={message ? descId : undefined}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md animate-scale-in rounded-2xl bg-white p-6 shadow-dramatic"
      >
        <div className="flex items-start gap-4">
          <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${iconWrap}`}>
            <ExclamationTriangleIcon className="h-6 w-6" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h3 id={titleId} className="text-base font-bold text-dark-900">{title}</h3>
            {message && (
              <div id={descId} className="mt-1.5 text-sm leading-relaxed text-surface-500 whitespace-pre-line">
                {message}
              </div>
            )}
            {children}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2.5">
          <button
            ref={cancelRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!busy) runClose(onCancel);
            }}
            disabled={busy}
            className="inline-flex min-h-11 cursor-pointer items-center rounded-lg border border-surface-200 px-4 py-2 text-sm font-semibold text-dark-700 transition-colors duration-200 hover:bg-surface-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60 ${confirmCls}`}
          >
            {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
